import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useSelector } from "react-redux";

import { UserContext, getIsSigningOut } from "../amplify/authenticator-provider";
import { GET_GENERIC_FILTERS, GET_GENERIC_FILTER_TYPES } from "../graphql/query";
import { CREATE_GENERIC_FILTER, UPDATE_GENERIC_FILTER } from "../graphql/mutation";
import { getGenericFilterId } from "../library/utils";
import { getLocationId } from "../redux/slices/locationSlice";
import useDebounce from "./debounceHook";
import {
  STICKY_MODULES,
  decodeStickyEnvelope,
  encodeStickyEnvelope,
  getStickyLocationId,
  getStickySessionNonce,
  isImpersonating,
  stickyCacheKey,
} from "../helpers/stickyFilters";

/**
 * EAI-1889 — persist the user's CURRENTLY APPLIED filters server-side and
 * re-apply them on the next load.
 *
 * See `src/helpers/stickyFilters.js` for the storage contract (reserved names,
 * envelope, why we never touch mobile's record).
 *
 * Usage:
 *
 *   const { restorePending } = useStickyFilters({
 *     moduleKey: STICKY_MODULES.ACTIVITY_CENTER.key,
 *     currentFilters: pickStickyFilters(moduleKey, { ...filters, leadSort }),
 *     onRestore: restored => dispatch(applyLeadFilters(restored)),
 *     enabled: true,
 *   });
 *
 * The consumer must add `restorePending` to its list query's `skip`, so the
 * first request carries the restored filters instead of firing twice.
 *
 * Saving is silent by design: debounced, fire-and-forget, never a toast and
 * never a refetch of the list.
 */

/**
 * Last payload we either restored FROM or wrote TO the server, per
 * user + location + module.
 *
 * Module scope is deliberate. `EngagementActivity` remounts on every tab switch
 * (`destroyInactiveTabPane`), and a component-scoped ref would make each
 * remount look like a fresh session and re-apply the stored set over whatever
 * the user had just changed. A real page load clears this map, so a reload
 * still picks up changes made on another device.
 */
const syncedPayloads = new Map();

/** Backend returns 208 when a record with this (user, location, name, type) already exists. */
const ALREADY_REPORTED = 208;

export const resetStickyFilterCache = () => syncedPayloads.clear();

const useStickyFilters = ({
  moduleKey,
  currentFilters,
  onRestore,
  enabled = true,
  readOnly = false,
}) => {
  const moduleConfig = STICKY_MODULES[moduleKey];
  const { user } = useContext(UserContext) || {};
  const userId = user?.id ?? null;

  // The redux location id is only a CHANGE SIGNAL — it is null on a fresh mount
  // and lags in-tab navigation. Reading it keeps this hook re-rendering on a
  // location switch; the id we actually use comes from sessionStorage.
  useSelector(getLocationId);
  const locationId = getStickyLocationId();

  // The cache key whose stored set has been read (or failed to read). Compared
  // against the current key to derive `restorePending` — see below.
  const [resolvedKey, setResolvedKey] = useState(null);
  // Safety valve: if the prerequisites or the read never arrive, stop gating the
  // caller's query rather than leaving the screen empty forever.
  const [gateExpired, setGateExpired] = useState(false);

  const recordIdRef = useRef(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  // Serialises writes so two quick changes can't land out of order.
  const writeChainRef = useRef(Promise.resolve());
  // The payload waiting behind the debounce, flushed if we unmount first.
  const pendingWriteRef = useRef(null);
  const saveRef = useRef(null);

  const { data: typesData } = useQuery(GET_GENERIC_FILTER_TYPES, {
    skip: !enabled || !moduleConfig,
    fetchPolicy: "cache-first",
  });
  const genericFilterTypeId = getGenericFilterId(
    typesData?.getGenericFilterTypes || [],
    moduleConfig?.mode
  );

  const canRead = Boolean(enabled && moduleConfig && genericFilterTypeId && userId);
  // Read per render, not once per mount: logging out clears sessionStorage, so
  // the next sign-in must see a different key even if this component never
  // unmounted (logout is an SPA navigate, not a page load).
  const cacheKey = moduleConfig
    ? stickyCacheKey(moduleKey, userId, locationId, getStickySessionNonce())
    : null;

  /**
   * Hold the caller's list query until this user's stored set has been read.
   *
   * DERIVED, deliberately. An earlier version kept this in state and lowered it
   * from an effect (`setRestorePending(canRead)`), which opened the gate during
   * the window where `canRead` is still false because the filter-type query or
   * the user context hasn't arrived. On a hard refresh that window is invisible
   * (the types come from the persisted Apollo cache), but a sign-out clears that
   * cache — so on the next sign-in the types had to come from the network, the
   * gate opened early, and the list fetched unfiltered before the stored
   * filters were read. Derived state cannot fall open like that: it only clears
   * when THIS key has resolved, or when the safety valve fires.
   */
  const restorePending = Boolean(
    enabled && moduleConfig && !gateExpired && (!canRead || resolvedKey !== cacheKey)
  );

  const [createGenericFilter] = useMutation(CREATE_GENERIC_FILTER);
  const [updateGenericFilter] = useMutation(UPDATE_GENERIC_FILTER);
  const [fetchGenericFilters] = useLazyQuery(GET_GENERIC_FILTERS, {
    fetchPolicy: "network-only",
  });

  /** The record for THIS module, for this user, out of a getGenericFilters result. */
  const findOwnRecord = useCallback(
    records =>
      (records || []).find(
        record =>
          record?.filterName === moduleConfig?.filterName &&
          (userId == null || record?.userId == null || +record.userId === +userId)
      ) || null,
    [moduleConfig, userId]
  );

  // --- read + restore -------------------------------------------------------

  // A new user, location or session means a different stored record.
  useEffect(() => {
    recordIdRef.current = null;
  }, [cacheKey]);

  // Safety valve for the gate. If the filter types, the user context or the read
  // itself never arrive, the caller's list query would be held indefinitely and
  // the screen would sit empty. Give the whole restore this long, then let the
  // caller through with whatever filters it has.
  useEffect(() => {
    if (!enabled || !moduleConfig) return undefined;
    setGateExpired(false);
    const timer = setTimeout(() => setGateExpired(true), 6000);
    return () => clearTimeout(timer);
  }, [enabled, moduleConfig, cacheKey]);

  const {
    data: storedData,
    loading: storedLoading,
    error: storedError,
  } = useQuery(GET_GENERIC_FILTERS, {
    skip: !canRead,
    variables: {
      genericFilterTypeId,
      filterName: moduleConfig?.filterName,
      locationId,
    },
    fetchPolicy: "network-only",
  });

  // Deliberately an effect over the result rather than `onCompleted`: from
  // Apollo 3.8 on, `onCompleted` is not guaranteed to fire for a repeat result,
  // and a missed call would leave `restorePending` true forever — which would
  // block the consumer's list query. This runs off state we can always see.
  // It is idempotent: `syncedPayloads` makes a second pass a no-op.
  useEffect(() => {
    if (!canRead || storedLoading) return;
    if (storedError) {
      // Never block the screen on this — the user just gets default filters.
      console.log("useStickyFilters: failed to load stored filters", storedError);
      setResolvedKey(cacheKey);
      return;
    }
    if (!storedData) return;
    try {
      const record = findOwnRecord(storedData?.getGenericFilters);
      recordIdRef.current = record?.id ?? null;
      const stored = record ? decodeStickyEnvelope(record?.genericFilterJson?.filterJson) : null;
      const serialized = JSON.stringify(stored || {});
      // Unchanged since the last set we applied or wrote in this page load:
      // nothing to restore, and re-applying would undo edits made since.
      if (stored && syncedPayloads.get(cacheKey) !== serialized) {
        syncedPayloads.set(cacheKey, serialized);
        onRestoreRef.current?.(stored);
      } else if (!stored && !syncedPayloads.has(cacheKey)) {
        // No stored state yet. Record the baseline so the user's first change
        // is what creates the record.
        syncedPayloads.set(cacheKey, null);
      }
    } catch (err) {
      console.log("useStickyFilters: failed to restore filters", err);
    } finally {
      // This key is now resolved, which is what opens the gate.
      setResolvedKey(cacheKey);
    }
  }, [canRead, storedLoading, storedError, storedData, cacheKey, findOwnRecord]);

  // --- write ----------------------------------------------------------------

  const persist = useCallback(
    async serialized => {
      const variables = {
        filterJson: JSON.stringify(encodeStickyEnvelope(JSON.parse(serialized))),
        // UpdateGenericFilter OVERWRITES filter_name with whatever it is given
        // (defaulting to "DEFAULT"), so the reserved name goes on every write.
        filterName: moduleConfig.filterName,
        genericFilterTypeId,
        // Deliberately false: we look this record up by name, and `true` makes
        // the backend clear is_current on every other record of this
        // (user, location, type) — which the preset dropdowns round-trip.
        isCurrent: false,
        locationId,
      };

      if (recordIdRef.current) {
        await updateGenericFilter({ variables: { ...variables, id: +recordIdRef.current } });
        return;
      }

      const res = await createGenericFilter({ variables });
      const result = res?.data?.createGenericFilter;
      if (result?.statusCode === ALREADY_REPORTED) {
        // Another tab (or an earlier session) created it first. Adopt that
        // record rather than giving up on the write.
        const existing = await fetchGenericFilters({
          variables: {
            genericFilterTypeId,
            filterName: moduleConfig.filterName,
            locationId,
          },
        });
        const record = findOwnRecord(existing?.data?.getGenericFilters);
        if (!record?.id) return;
        recordIdRef.current = record.id;
        await updateGenericFilter({ variables: { ...variables, id: +record.id } });
        return;
      }
      if (result?.statusCode === 200) {
        recordIdRef.current = result?.genericFilters?.id ?? null;
      }
    },
    [
      createGenericFilter,
      updateGenericFilter,
      fetchGenericFilters,
      findOwnRecord,
      genericFilterTypeId,
      locationId,
      moduleConfig,
    ]
  );

  const save = useCallback(
    serialized => {
      pendingWriteRef.current = null;
      writeChainRef.current = writeChainRef.current
        .then(() => persist(serialized))
        .catch(err => {
          // Logged, never surfaced. The next change retries.
          console.log("useStickyFilters: failed to save filters", err);
        });
      return writeChainRef.current;
    },
    [persist]
  );
  saveRef.current = save;

  const debouncedSave = useDebounce(save, 800, [save]);

  // `useDebounce` clears its timer on unmount, and this component unmounts on
  // every Activity Center tab switch — so a change made in the last 800ms
  // would otherwise be dropped. Flush it instead.
  useEffect(
    () => () => {
      // Not during sign-out: sessionStorage is already cleared by then, so the
      // write would land on whatever location the backend falls back to.
      if (pendingWriteRef.current && !getIsSigningOut()) {
        saveRef.current?.(pendingWriteRef.current);
      }
    },
    []
  );

  // Two cases where a filter change must NOT be written back:
  //
  // - Signing out. `layoutHelpers` dispatches resetLeadFilters() and
  //   resetOpportunityState() while these screens are still mounted, and it
  //   clears sessionStorage first. Without this guard every logout would write
  //   an empty filter set — over the user's own record, at whatever location
  //   the backend defaults to — and silently undo the whole feature. The list
  //   query guards itself the same way.
  // - Impersonation. An admin acting as someone else reads that person's
  //   filters but must never overwrite them.
  // Never write before we have read: the local filter state at that point is
  // whatever the screen mounted with (empty, after a sign-out purge), and
  // writing it would destroy the stored set we were about to restore. Keyed on
  // `resolvedKey` rather than on `restorePending`, because the gate's safety
  // valve can open that without a read ever having completed.
  //
  // `readOnly` consumers restore the stored set but never write to it. The
  // browser extension is one: it clears the campaign and user filters itself on
  // every location hydration, so saving from there would persist that
  // housekeeping over the set the web app restores.
  const readResolved = resolvedKey === cacheKey;
  const canWrite = canRead && readResolved && !readOnly && !getIsSigningOut() && !isImpersonating();
  const serializedFilters = JSON.stringify(currentFilters || {});

  useEffect(() => {
    if (!canWrite) return;
    // Nothing changed since the set we last restored or wrote.
    if (syncedPayloads.get(cacheKey) === serializedFilters) return;
    // Recorded before the request so a re-render mid-flight can't queue the
    // same payload twice.
    syncedPayloads.set(cacheKey, serializedFilters);
    pendingWriteRef.current = serializedFilters;
    debouncedSave(serializedFilters);
  }, [canWrite, cacheKey, serializedFilters, debouncedSave]);

  /** Flush the current set immediately, bypassing the debounce. */
  const saveNow = useCallback(() => {
    if (!canWrite) return Promise.resolve();
    syncedPayloads.set(cacheKey, serializedFilters);
    return save(serializedFilters);
  }, [canWrite, cacheKey, serializedFilters, save]);

  return { restorePending, saveNow };
};

export default useStickyFilters;
